import { useQuery } from "@tanstack/react-query";

import { getPracticeManagementOrganicFertilizerParameter, getPracticeManagementOrganicFertilizerParameters } from "services";

import { DEFAULT_CACHE_TIME, PRACTICE_MANAGEMENT, PRACTICE_MANAGEMENT_ORGANIC_FERTILIZER_PARAMETERS } from "consts";

export const usePracticeManagementOrganicFertilizerParameters = (country: PracticeManagement.CountryCode = "global") =>
  useQuery({
    queryKey: [PRACTICE_MANAGEMENT, PRACTICE_MANAGEMENT_ORGANIC_FERTILIZER_PARAMETERS, country],
    queryFn: async () => {
      const parameters = await getPracticeManagementOrganicFertilizerParameters(country);
      return parameters.map((parameter) => ({
        ...parameter,
        editingKey: parameter.fertilizerId,
      }));
    },
    staleTime: 1000 * 60 * 60, // 1h
    gcTime: DEFAULT_CACHE_TIME,
  });

/**
 * @function usePracticeManagementOrganicFertilizerParameter
 * @description Fetch the organic fertilizer parameter (using fallbacks where appropriate)
 * for the specified fertilizer (with optional version / country)
 * @param fertilizerId Organic fertilizer ID
 * @param version (Optional) version query for the practice management service
 * @param country (Optional) country query for the practice management service
 * @returns useQuery QueryResult for the requested organic fertilizer parameters; data is null when no parameter exists for the fertilizer
 */
export const usePracticeManagementOrganicFertilizerParameter = (fertilizerId: string, country: PracticeManagement.CountryCode = "global") =>
  useQuery({
    queryKey: [PRACTICE_MANAGEMENT, PRACTICE_MANAGEMENT_ORGANIC_FERTILIZER_PARAMETERS, fertilizerId, country],
    // react-query treats a queryFn resolving to undefined as an error, so map a missing record (404) to null
    queryFn: async () => (await getPracticeManagementOrganicFertilizerParameter(fertilizerId, country)) ?? null,
    enabled: !!fertilizerId,
    staleTime: 1000 * 60 * 60, // 1h
    gcTime: DEFAULT_CACHE_TIME,
  });
